/**
 * Локаль календаря и чисел — одна на все поля формы.
 *
 * Календарь говорит на языке интерфейса: `ru → ru-RU`, `uz-Latn → uz-Latn-UZ`,
 * `uz-Cyrl → uz-Cyrl-UZ` (месяцы и дни недели той же письменностью; неделя с
 * понедельника задаётся полем, а не локалью). Раньше `AnalysisControls` давал
 * `uz-UZ` обоим узбекским, и кириллица в календаре не появлялась.
 *
 * Числа форматируются шаблоном `ru-RU` для всех трёх языков: узбекский CLDR
 * пишет их так же («8 107,5»), а урезанный ICU в части браузеров отдаёт
 * английское «8,107.5» (см. `i18n/runtime.ts`).
 */
import { parseDate, CalendarDate } from '@internationalized/date';
import { useIntlSafe } from '../../../i18n/hooks';
import { isLocale, SOURCE_LOCALE, type Locale } from '../../../i18n/locales';

export type CalendarLocale = 'ru-RU' | 'uz-Latn-UZ' | 'uz-Cyrl-UZ';

export const NUMBER_LOCALE = 'ru-RU';

const CALENDAR: Record<Locale, CalendarLocale> = {
  ru: 'ru-RU',
  'uz-Latn': 'uz-Latn-UZ',
  'uz-Cyrl': 'uz-Cyrl-UZ',
};

/** Локаль `Intl` для календаря по языку интерфейса. */
export function calendarLocale(locale: string): CalendarLocale {
  return CALENDAR[isLocale(locale) ? locale : SOURCE_LOCALE];
}

/** То же из текущего провайдера языка (реактивно). */
export function useCalendarLocale(): CalendarLocale {
  return calendarLocale(useIntlSafe().locale);
}

/**
 * Разбор ISO без исключения наружу: значение приходит из URL или сервера, и
 * `?as_of=вчера` не должно ронять экран.
 */
export function parseIso(value: string | null | undefined): CalendarDate | null {
  if (!value) return null;
  try {
    return parseDate(value);
  } catch {
    return null;
  }
}

/** Сегодня по местному календарю, ISO `ГГГГ-ММ-ДД` (не UTC: в 03:00 по Ташкенту UTC ещё «вчера»). */
export function todayIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
